import React, { useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AddressAutocomplete } from "components/common/AddressAutocomplete";
import { InputBase, InputLabel as StandardInputLabel } from "components/common/Input";
import { Employee, EmployeeAssignedAsset } from "types/employee";
import EmployeeAssignedAssetSummary from "pages/Employees/EmployeeAssignedAssetSummary";
import { Location } from "types/location";

export const EMPLOYEE_STATUSES = [
  { value: "active", label: "Active" },
  { value: "offboarded", label: "Offboarded" },
  { value: "pending_offboard", label: "Pending offboard" },
];

export type EmployeeFormValues = {
  externalEmployeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  department: string;
  status: string;
  officeLocationId: string;
  homeAddress: string;
  homeAddress2: string;
  homeCity: string;
  homeState: string;
  homeZipcode: string;
  homeCountry: string;
  remoteWorker: boolean;
};

export function emptyEmployeeForm(): EmployeeFormValues {
  return {
    externalEmployeeId: "",
    firstName: "",
    lastName: "",
    email: "",
    jobTitle: "",
    department: "",
    status: "active",
    officeLocationId: "",
    homeAddress: "",
    homeAddress2: "",
    homeCity: "",
    homeState: "",
    homeZipcode: "",
    homeCountry: "",
    remoteWorker: false,
  };
}

export function employeeToForm(emp: Employee): EmployeeFormValues {
  const officeId = emp.officeLocation?.id ?? emp.officeLocationId;
  return {
    externalEmployeeId: emp.externalEmployeeId ?? "",
    firstName: emp.firstName || "",
    lastName: emp.lastName || "",
    email: emp.email || "",
    jobTitle: emp.jobTitle || "",
    department: emp.department || "",
    status: emp.status || "active",
    officeLocationId: officeId != null ? String(officeId) : "",
    homeAddress: emp.homeAddress ?? "",
    homeAddress2: emp.homeAddress2 ?? "",
    homeCity: emp.homeCity ?? "",
    homeState: emp.homeState ?? "",
    homeZipcode: emp.homeZipcode ?? "",
    homeCountry: emp.homeCountry ?? "",
    remoteWorker: Boolean(emp.remoteWorker),
  };
}

type EmployeeFormProps = {
  values: EmployeeFormValues;
  onChange: (v: EmployeeFormValues) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel: string;
  cancelTo: string;
  locations: Location[];
  /** When set (e.g. edit mode), list assigned assets; omit on “new” employee. */
  assignedAssets?: EmployeeAssignedAsset[] | null;
};

export default function EmployeeForm({
  values,
  onChange,
  onSubmit,
  submitLabel,
  cancelTo,
  locations,
  assignedAssets,
}: EmployeeFormProps) {
  const homeAddressInputId = useMemo(() => `emp-home-addr-${crypto.randomUUID()}`, []);

  const patch = (partial: Partial<EmployeeFormValues>) => {
    onChange({ ...values, ...partial });
  };

  const set =
    (field: keyof EmployeeFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      patch({ [field]: e.target.value } as Partial<EmployeeFormValues>);
    };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Employee ID (external)"
            value={values.externalEmployeeId}
            onChange={set("externalEmployeeId")}
            helperText="From your HR or IT system. Not Assettech’s internal id."
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="First name" value={values.firstName} onChange={set("firstName")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Last name" value={values.lastName} onChange={set("lastName")} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Email" type="email" value={values.email} onChange={set("email")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Job title" value={values.jobTitle} onChange={set("jobTitle")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Department" value={values.department} onChange={set("department")} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="emp-status-label">Status</InputLabel>
            <Select
              labelId="emp-status-label"
              label="Status"
              value={values.status}
              onChange={(e) => patch({ status: e.target.value })}
            >
              {EMPLOYEE_STATUSES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.remoteWorker}
                onChange={(_, checked) => patch({ remoteWorker: checked })}
              />
            }
            label="Remote worker"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
            Office location
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Optional. Choose a site from your{" "}
            <RouterLink to="/locations">Locations</RouterLink> directory.
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="emp-office-loc-label">Office location</InputLabel>
            <Select
              labelId="emp-office-loc-label"
              label="Office location"
              value={values.officeLocationId}
              onChange={(e) => patch({ officeLocationId: e.target.value })}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.uuid || loc.id} value={String(loc.id)}>
                  {loc.name || loc.address || loc.city || `Location ${loc.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
            Home address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stored on the employee record. Search for an address to auto-fill the fields, or enter them manually.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              mb: 2,
            }}
          >
            <AddressAutocomplete
              inputId={homeAddressInputId}
              label="Address"
              value={values.homeAddress}
              onChange={(v) => patch({ homeAddress: v })}
              onAddressSelect={(fields) => {
                if (typeof fields === "string") return;
                patch({
                  homeAddress: fields.address,
                  homeAddress2: fields.address2,
                  homeCity: fields.city,
                  homeState: fields.state,
                  homeZipcode: fields.zipcode,
                  homeCountry: fields.country,
                });
              }}
            />

            <FormControl variant="standard" fullWidth>
              <StandardInputLabel shrink htmlFor={`${homeAddressInputId}-line2`}>
                Address line 2
              </StandardInputLabel>
              <InputBase
                id={`${homeAddressInputId}-line2`}
                placeholder="Suite, unit, building"
                value={values.homeAddress2}
                onChange={(e) => patch({ homeAddress2: e.target.value })}
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 3,
              mb: 2,
            }}
          >
            <FormControl variant="standard" fullWidth>
              <StandardInputLabel shrink htmlFor={`${homeAddressInputId}-city`}>
                City
              </StandardInputLabel>
              <InputBase
                id={`${homeAddressInputId}-city`}
                placeholder="City"
                value={values.homeCity}
                onChange={(e) => patch({ homeCity: e.target.value })}
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <StandardInputLabel shrink htmlFor={`${homeAddressInputId}-state`}>
                State / province
              </StandardInputLabel>
              <InputBase
                id={`${homeAddressInputId}-state`}
                placeholder="State"
                value={values.homeState}
                onChange={(e) => patch({ homeState: e.target.value })}
              />
            </FormControl>
            <FormControl variant="standard" fullWidth>
              <StandardInputLabel shrink htmlFor={`${homeAddressInputId}-zip`}>
                Postal code
              </StandardInputLabel>
              <InputBase
                id={`${homeAddressInputId}-zip`}
                placeholder="Postal code"
                value={values.homeZipcode}
                onChange={(e) => patch({ homeZipcode: e.target.value })}
              />
            </FormControl>
          </Box>

          <FormControl variant="standard" fullWidth>
            <StandardInputLabel shrink htmlFor={`${homeAddressInputId}-country`}>
              Country
            </StandardInputLabel>
            <InputBase
              id={`${homeAddressInputId}-country`}
              placeholder="Country"
              value={values.homeCountry}
              onChange={(e) => patch({ homeCountry: e.target.value })}
            />
          </FormControl>
        </Grid>

        {assignedAssets != null ? (
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
              Assigned assets
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              To assign or remove assets, open the asset and set the employee there.
            </Typography>
            {assignedAssets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No assets assigned to this employee.
              </Typography>
            ) : (
              <Box sx={{ m: 0 }}>
                {assignedAssets.map((a) => (
                  <EmployeeAssignedAssetSummary key={a.uuid} asset={a} />
                ))}
              </Box>
            )}
          </Grid>
        ) : null}
      </Grid>
      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button type="submit" variant="contained">
          {submitLabel}
        </Button>
        <Button component={RouterLink} to={cancelTo} variant="outlined">
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
